import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Container, Typography, Stack, TextField } from "@mui/material";
// components
import Page from "../components/Page";
import TimePeriodSelector from "../components/common/TimePeriodSelector";
import {
  CollapsibleCard,
  ConsumptionTrendChart,
  ChargersV2,
  ChargerLocationsMap,
  NetworkKpiRow,
  PeakHourBar,
  EnvironmentalImpactV2,
  VehicleChargingV2,
} from "../components/_dashboard/v2";
import { useVehicleChargingEnabled } from "src/hooks/useVehicleChargingEnabled";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useList } from "src/hooks/useFetch.hook";
import { useTransactionsWithDateRange } from "src/hooks/useTransactionsWithDateRange.hook";
import { useNetworkSummary } from "src/hooks/useNetworkSummary.hook";
import { usePeakHourHeatmap } from "src/hooks/usePeakHourHeatmap.hook";
import useTeamCarbonConfig from "src/hooks/useTeamCarbonConfig.hook";
import { is } from "src/util/is";

// Feature-flagged dashboard view (see src/utils/featureFlags.js). Built entirely
// from the reusable components under src/components/_dashboard/v2.
//
// There is no global time period: each section owns its own time control — the
// trend cards via Daily/Weekly/Monthly, Carbon and Station Uptime via their own
// Time Period selector.

const SEVEN_DAY_INTERVAL = () => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 6);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return { start: start.toISOString(), end: end.toISOString(), type: "Last7Days" };
};

const computeInterval = (period) => {
  const now = new Date();
  let start;
  let end = new Date(now);
  end.setHours(23, 59, 59, 999);

  switch (period) {
    case "Today":
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      break;
    case "Last30Days":
      start = new Date(now);
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      break;
    case "Last90Days":
      start = new Date(now);
      start.setDate(start.getDate() - 89);
      start.setHours(0, 0, 0, 0);
      break;
    case "Last7Days":
    default:
      start = new Date(now);
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      break;
  }
  return { start: start.toISOString(), end: end.toISOString(), type: period };
};

const TIME_PERIOD_OPTIONS = [
  { value: "Today", label: "Today" },
  { value: "Last7Days", label: "Last 7 Days" },
  { value: "Last30Days", label: "Last 30 Days" },
  { value: "Last90Days", label: "Last 90 Days" },
];

// KPI section additionally offers a custom date range (capped at 90 days).
const KPI_PERIOD_OPTIONS = [...TIME_PERIOD_OPTIONS, { value: "Custom", label: "Custom" }];
const MAX_RANGE_DAYS = 90;

// Local YYYY-MM-DD (not UTC) for date inputs.
const localDateStr = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const DashboardAppV2 = () => {
  const navigate = useNavigate();
  const { teamId } = useSelectedTeam();
  // Company flag (Company → Settings → Vehicle Charging), inherited from the parent.
  const vehicleChargingEnabled = useVehicleChargingEnabled(teamId);
  const { constants: carbonConstants } = useTeamCarbonConfig(teamId);
  // Request the enriched charger list (connectors/status/firmware/utilization).
  // Other callers use the default lightweight list.
  const { data: chargers, isFetching: chargersFetching } = useList(
    "chargers",
    undefined,
    undefined,
    { limit: 500, enrich: true }
  );
  // When the charger list last arrived — shown in the header as "Updated hh:mm".
  // Keyed on the fetch finishing: useList returns a new array on every render, so
  // depending on `chargers` itself would loop.
  const [updatedAt, setUpdatedAt] = useState(null);
  const hasChargers = Boolean(chargers);
  useEffect(() => {
    if (!chargersFetching && hasChargers) setUpdatedAt(new Date());
  }, [chargersFetching, hasChargers]);

  // Failed Rate tile -> the Transactions page's Failed tab, over the widest
  // window so the user sees all recent failures (0-kWh sessions + payments with
  // no session), not just the last 7 days.
  const handleKpiTileClick = (key) => {
    if (key === "failedRateToday") {
      navigate("/transactions?tab=failed&frange=last90days");
    }
  };

  // Carbon section owns its own time period.
  const [carbonPeriod, setCarbonPeriod] = useState("Last7Days");
  const [carbonInterval, setCarbonInterval] = useState(SEVEN_DAY_INTERVAL());

  const handleCarbonPeriod = (p) => {
    setCarbonPeriod(p);
    setCarbonInterval(computeInterval(p));
  };

  // Reset the carbon period to Last 7 Days whenever the selected team changes.
  useEffect(() => {
    setCarbonPeriod("Last7Days");
    setCarbonInterval(SEVEN_DAY_INTERVAL());
  }, [teamId]);

  // Top KPI section (Energy/Revenue/Sessions/Failed) owns its own time period.
  const [kpiPeriod, setKpiPeriod] = useState("Today");
  const [kpiInterval, setKpiInterval] = useState(() => computeInterval("Today"));
  const [kpiCustomFrom, setKpiCustomFrom] = useState("");
  const [kpiCustomTo, setKpiCustomTo] = useState("");
  const [kpiCustomError, setKpiCustomError] = useState("");

  // Validate a custom range and, if valid, apply it as the KPI interval.
  const applyKpiCustomRange = (from, to) => {
    if (!from || !to) {
      setKpiCustomError("");
      return;
    }
    const f = new Date(from);
    f.setHours(0, 0, 0, 0);
    const t = new Date(to);
    t.setHours(23, 59, 59, 999);
    if (f > t) {
      setKpiCustomError("From date must be on or before To date");
      return;
    }
    const diffDays = Math.round((t - f) / (1000 * 60 * 60 * 24));
    if (diffDays > MAX_RANGE_DAYS) {
      setKpiCustomError(`Range cannot exceed ${MAX_RANGE_DAYS} days`);
      return;
    }
    setKpiCustomError("");
    setKpiInterval({ start: f.toISOString(), end: t.toISOString(), type: "Custom" });
  };

  const handleKpiPeriod = (p) => {
    setKpiPeriod(p);
    if (p === "Custom") {
      // Keep the current interval until a valid custom range is chosen.
      applyKpiCustomRange(kpiCustomFrom, kpiCustomTo);
      return;
    }
    setKpiCustomError("");
    setKpiInterval(computeInterval(p));
  };

  // Reset the KPI period to Today whenever the selected team changes.
  useEffect(() => {
    setKpiPeriod("Today");
    setKpiInterval(computeInterval("Today"));
    setKpiCustomFrom("");
    setKpiCustomTo("");
    setKpiCustomError("");
  }, [teamId]);

  // Carbon section fetches its own transactions for its period.
  const { data: carbonTxns, isFetching: carbonFetching } = useTransactionsWithDateRange({
    teamId,
    timeInterval: carbonInterval,
    enabled:
      !is.empty(carbonInterval.start) &&
      !is.empty(carbonInterval.end) &&
      !is.empty(carbonInterval.type),
  });

  const chargerList = useMemo(
    () => (Array.isArray(chargers) ? chargers : []),
    [chargers]
  );


  const { data: networkSummary, isFetching: summaryFetching } = useNetworkSummary({
    teamId,
    timeInterval: kpiInterval,
  });
  const { data: peakHeatmap, isFetching: heatmapFetching } = usePeakHourHeatmap({ teamId });

  return (
    <Page title="Dashboard">
      <Container maxWidth="xl" sx={{ "@media screen and (min-width:600px)": { padding: 0 } }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ mb: { xs: 2, sm: 3 }, flexWrap: "wrap", rowGap: 1.5 }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{ color: "text.secondary", fontWeight: 500, fontSize: { xs: "1rem", md: "1.25rem" } }}
            >
              InsightHub 🔎 – AI Analytics
            </Typography>
            {updatedAt && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Updated {updatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* 1. Network overview — KPI tiles with their own time period */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: kpiCustomError ? 0.5 : 1, flexWrap: "wrap", rowGap: 1 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" } }}>
              Network Overview
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            {kpiPeriod === "Custom" && (
              <>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={kpiCustomFrom}
                  inputProps={{ max: kpiCustomTo || localDateStr() }}
                  onChange={(e) => {
                    setKpiCustomFrom(e.target.value);
                    applyKpiCustomRange(e.target.value, kpiCustomTo);
                  }}
                  sx={{ width: 160 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={kpiCustomTo}
                  inputProps={{ min: kpiCustomFrom || undefined, max: localDateStr() }}
                  onChange={(e) => {
                    setKpiCustomTo(e.target.value);
                    applyKpiCustomRange(kpiCustomFrom, e.target.value);
                  }}
                  sx={{ width: 160 }}
                />
              </>
            )}
              <TimePeriodSelector
                value={kpiPeriod}
                onChange={handleKpiPeriod}
                options={KPI_PERIOD_OPTIONS}
              />
            </Stack>
          </Stack>
          {kpiPeriod === "Custom" && kpiCustomError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", textAlign: "right", mb: 1 }}
            >
              {kpiCustomError}
            </Typography>
          )}
          <NetworkKpiRow
            summary={networkSummary}
            isLoading={summaryFetching}
            onTileClick={handleKpiTileClick}
            clickableKeys={["failedRateToday"]}
            period={kpiPeriod}
          />
        </Box>

        {/* 2. Chargers — Status tab (tiles filter the inline list) and
            Uptime tab (real online/offline time, own time period) */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <ChargersV2 chargers={chargerList} isFetching={chargersFetching} teamId={teamId} />
        </Box>

        {/* 3. Fleet: Vehicle Charging (charging now + history, matched by battery MAC).
            Only when the company flag is on (Company → Settings → Vehicle Charging). */}
        {vehicleChargingEnabled && (
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <VehicleChargingV2 key={`vehicle-charging-${teamId}`} teamId={teamId} />
          </Box>
        )}

        {/* 4. Energy, Session and Revenue — one compact row, each with its
            own Daily / Weekly / Monthly control. Energy also has a collapsible bar view. */}
        <Grid container spacing={{ xs: 2, sm: 2.5 }} sx={{ mb: { xs: 2, sm: 3 } }} alignItems="flex-start">
          <Grid item xs={12} md={4}>
            <ConsumptionTrendChart
              key={`energy-${teamId}`}
              title="Energy Consumption"
              subtitle="Energy usage over time"
              metric="energy"
              teamId={teamId}
              compact
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ConsumptionTrendChart
              key={`sessions-${teamId}`}
              title="Session Information"
              subtitle="Charging sessions over time"
              metric="sessions"
              teamId={teamId}
              compact
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ConsumptionTrendChart
              key={`revenue-${teamId}`}
              title="Revenue Information"
              subtitle="Revenue collected over time"
              metric="revenue"
              teamId={teamId}
              compact
            />
          </Grid>
        </Grid>

        {/* Peak hours + Charger locations, side by side */}
        <Grid container spacing={{ xs: 2, sm: 2.5 }} sx={{ mb: { xs: 2, sm: 3 } }} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <CollapsibleCard title="Peak Hours" subtitle="Sessions by hour of day (last 30 days)">
              <PeakHourBar heatmap={peakHeatmap} isLoading={heatmapFetching} height={320} />
            </CollapsibleCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <CollapsibleCard
              title="Charger Locations"
              subtitle="Click a charger on the map to view its power and status"
            >
              <ChargerLocationsMap
                chargers={chargerList}
                height={340}
                teamId={teamId}
                showVehicles={vehicleChargingEnabled}
              />
            </CollapsibleCard>
          </Grid>
        </Grid>

        {/* 6. Carbon emission — redesigned, own time period */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <EnvironmentalImpactV2
            key={`environmental-${teamId}-${carbonInterval.type}`}
            timeInterval={carbonInterval}
            transactionsData={carbonTxns}
            isLoading={carbonFetching}
            carbonConstants={carbonConstants}
            headerAction={
              <TimePeriodSelector
                value={carbonPeriod}
                onChange={handleCarbonPeriod}
                options={TIME_PERIOD_OPTIONS}
              />
            }
          />
        </Box>

      </Container>
    </Page>
  );
};

export default DashboardAppV2;
